import HotelAutocomplete from 'components/ReusableComponents/HotelAutocomplete'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import SearchEngines from 'features/NewHotel/BookingEngine/SearchEngines'
import { BEs, parentChains, PMSnames, PMSs } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import dashboardApi from 'services/api/dashboard'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'
import { getBEs } from 'utils/manageHotel/beHelper'

import Dropdown from '../../../components/ReusableComponents/ManageHotel/Dropdown/index'
import FormGroup from '../../../components/ReusableComponents/ManageHotel/FormGroup/index'
import { supportedLocales } from '../../../main/commonStatic'
import styles from './AutoInviteInfo.module.scss'
import ReservationHotlineInput from './ReservationHotline/index'
import ShortLinkHelper from './ShortLinkHelper/index'
import ShortLinkInput from './ShortLinkInput/index'
import TASearch from './TASearch/index'

const AutoInviteInfo = ({
  formData: {
    urlKey,
    reservationHotLine,
    bookingEngineLink,
    reviewTripAdvisorPage,
    cultureCode,
    chainCode,
    parentChainCode,
    stateCode,
    pms,
    city,
    countryCode,
    streetAddress,
    externalHotelId,
    bookingEngine,
    serviceStartDate,
    bookingEngineSlug,
    primaryWebsiteUrl
  },
  handleFormChange,
  setFormData,
  hotelName,
  handleValidated,
  valid,
  limited
}) => {
  const [shortLinkValid, setShortLinkValid] = useState()
  const [shortLinkError, setShortLinkError] = useState()

  const [isPhoneValid, setPhoneValid] = useState()

  const { addToast } = useToasts()

  useEffect(() => {
    const hasStreetAddress = !!String(streetAddress ?? '').trim()
    const isValid =
      shortLinkValid && isPhoneValid && !!reviewTripAdvisorPage && hasStreetAddress
    handleValidated(isValid)
  }, [
    handleValidated,
    isPhoneValid,
    reviewTripAdvisorPage,
    shortLinkValid,
    streetAddress
  ])

  useEffect(() => {
    if (Object.keys(getBEs(parentChainCode)).includes(bookingEngine)) return

    if (Object.keys(getBEs(parentChainCode)).length === 1) {
      setFormData({ bookingEngine: Object.keys(getBEs(parentChainCode))[0] })
      return
    }

    setFormData({ bookingEngine: '', bookingEngineSlug: '', bookingEngineLink: '' })
  }, [parentChainCode, setFormData, bookingEngine])

  async function handleIndieBEurl(e) {
    const value = e.target.value
    if (!value) return

    if (value.includes('skd-property-code') || value.includes('activeBookingEngine=KBE')) {
      const urlParams = new URLSearchParams(value.split('?')[1])
      const externalHotelId =
        urlParams.get('skd-property-code') || urlParams.get('propertyCode') || ''
      const bookingEngineSlug = value.match(/https?:\/\/([^\/\?]+)/)?.[1] || ''

      setFormData({
        bookingEngine: BEs.seekda,
        externalHotelId,
        bookingEngineSlug,
        bookingEngineLink: value
      })
      return
    }

    if (value.includes('namastay=true')) {
      const bookingEngineSlug = value.match(/https?:\/\/([^\/\?]+)/)?.[1] || ''
      setFormData({
        bookingEngine: BEs.namastay,
        bookingEngineSlug
      })
      return
    }

    if (value.includes('verticalbooking.com')) {
      const urlParams = new URLSearchParams(value.split('?')[1])
      const externalHotelId = urlParams.get('id_albergo')
      const dc = urlParams.get('dc')
      const idStile = urlParams.get('id_stile')
      const bookingEngineSlug = `&dc=${dc}&id_stile=${idStile}`

      setFormData({
        bookingEngine: BEs.verticalbooking,
        externalHotelId,
        bookingEngineSlug
      })
      return
    }

    if (value.includes('selfbook=true')) {
      const urlParams = new URLSearchParams(value.split('?')[1])
      const externalHotelId = urlParams.get('hotel')
      const bookingEngineSlug = value.match(/https?:\/\/([^\?]+)/)?.[1] || ''

      setFormData({
        bookingEngine: BEs.selfbook,
        externalHotelId,
        bookingEngineSlug
      })
      return
    }

    if (value.includes('rates-room1')) {
      const urlParams = new URLSearchParams(value.split('?')[1])
      const externalHotelId = urlParams.get('hotel') || ''
      const bookingEngineSlug =
        value.match(/https?:\/\/bookings\.([^\/\?]+)\.com/)?.[1] || ''

      setFormData({
        bookingEngine: BEs.p3,
        externalHotelId,
        bookingEngineSlug,
        bookingEngineLink: value
      })
      return
    }

    if (value.includes('marriott.com')) {
      const withoutHash = value.split('#')[0]
      const queryStart = withoutHash.indexOf('?')
      const queryString = queryStart >= 0 ? withoutHash.slice(queryStart + 1) : ''
      const urlParams = new URLSearchParams(queryString)
      const propertyCode = urlParams.get('propertyCode')
      if (propertyCode) {
        setFormData({
          bookingEngine: BEs.marriott,
          externalHotelId: propertyCode,
          bookingEngineLink: value
        })
        return
      }
    }

    if (value.includes('choicehotels.com/')) {
      const match = value.match(/choicehotels.com\/(.+)\/([a-z0-9]{5})?/)
      if (match) {
        setFormData({
          bookingEngine: BEs.choicehotels,
          bookingEngineSlug: match[1],
          externalHotelId: match[2]?.toUpperCase() || ''
        })
        return
      }
    }

    if (value.includes('{hotelExternalId}')) return

    if (value.includes('book/accommodations')) {
      const pageHTML = await dashboardApi.serverSideFetch(
        'wyndhamhotels.com',
        bookingEngineLink
      )

      const propertyId = pageHTML.match(/bookingEngineHotelId: '(\d+)'/)?.[1]

      setFormData({
        bookingEngine: BEs.travelclick_v2,
        bookingEngineSlug:
          value.match(/https?:\/\/(.+)\/book\/accommodations/)?.[1] || '',
        externalHotelId: propertyId
      })
    }

    if (value.includes('reztrip.com')) {
      setFormData({
        bookingEngine: 'reztrip',
        externalHotelId: value.match(/https?:\/\/(.+)\.reztrip\.com/)?.[1] || ''
      })
    }

    if (value.includes('booking.hotelkeyapp.com')) {
      const urlParams = new URLSearchParams(value.split('?')[1])

      const bookingEngineSlug = urlParams.get('pc')
      const externalHotelId = urlParams.get('property_id')

      setFormData({
        bookingEngine: BEs.hotelKey,
        bookingEngineSlug,
        externalHotelId
      })
      return
    }

    if (value.includes('?sbe_')) {
      let pageHTML = ''

      try {
        pageHTML = await dashboardApi.serverSideFetch('google.com', bookingEngineLink)
      } catch (e) {
        console.error(e)
      }

      const externalHotelId = pageHTML.match(/hotelId: "(.+)"/)?.[1]
      const bookingEngineSlug = value.match(/https:\/\/(.+)\/\?sbe_/)?.[1]

      setFormData({
        bookingEngine: BEs.skipper,
        externalHotelId,
        bookingEngineSlug
      })

      return
    }

    if (value.includes('bestwestern.com')) {
      setFormData({
        bookingEngine: 'bestwestern',
        externalHotelId: value.match(/.+.\/book\/.+\.(\d{5})/)?.[1] || '',
        pms: 'opera'
      })
    }

    if (value.includes('travelclick.com')) {
      setFormData({
        bookingEngine: 'travelclick',
        externalHotelId: value.match(/(?<=travelclick\.com\/)\d{6}/)?.[0] || ''
      })
    }

    if (value.includes('pegsbe.com/rooms')) {
      setFormData({
        bookingEngine: 'pegasus',
        externalHotelId: value.match(/(?<=https:\/\/)[a-z0-9]+/)[0],
        pms: 'skytouch',
        bookingEngineSlug: ''
      })
    }

    if (value.includes('secure.guestcentric.net')) {
      setFormData({
        bookingEngine: 'guestcentric',
        externalHotelId: value.match(/apikey=([^&]+)/)[1]
      })
    }

    if (value.includes('direct-book.com')) {
      setFormData({
        bookingEngine: 'direct-book',
        externalHotelId: value.match(/direct-book\.com.+properties\/(.+)\?/)?.[1] || '',
        bookingEngineSlug: value.match(/direct-book\.com.+properties\/(.+)\?/)?.[1] || ''
      })
    }

    if (value.includes('redroof.com')) {
      setFormData({
        bookingEngine: 'redroof',
        externalHotelId: value.match(/redroof.com\/.+DestId=(RR[A-Z0-9]+)/)[1]
      })
    }

    if (value.includes('stayntouch.website')) {
      setFormData({
        bookingEngine: BEs.stayntouch,
        externalHotelId: value.match(/(\w+)\.stayntouch\.website/)[1]
      })
    }

    // hotels\.cloudbeds\.com\/en\/reservation\/([^\/]+)

    if (value.includes('hotels.cloudbeds.com')) {
      setFormData({
        bookingEngine: BEs.cloudbeds,
        externalHotelId: value.match(
          /hotels\.cloudbeds\.com\/en\/reservation\/([^\/]+)/
        )[1]
      })
    }

    if (value.includes('/premium/index') && value.includes('id_albergo')) {
      const urlParams = new URLSearchParams(value.split('?')[1])

      const bookingEngineSlug = `&dc=${urlParams.get('dc')}`
      const externalHotelId = urlParams.get('id_albergo')

      setFormData({
        bookingEngine: BEs.visualmatrix,
        externalHotelId: externalHotelId,
        bookingEngineSlug: bookingEngineSlug
      })
    }

    if (value.includes('book.sonesta.com')) {
      addToast('Please use sonesta.com link on the home page of the hotel', {
        appearance: 'error'
      })
    }

    if (value.includes('sonesta.com') && !value.includes('book.sonesta.com')) {
      const params = value.match(/sonesta\.com\/([a-z-]+\/[a-z-]{2}\/[a-z-]+\/([a-z-]+))/)
      setFormData({
        bookingEngine: BEs.sonesta,
        bookingEngineSlug: params[1],
        externalHotelId: params[2]
      })
    }

    if (value.includes('karismahotels.com')) {
      const urlParams = new URLSearchParams(value.split('?')[1])
      const externalHotelId = urlParams.get('txtHotelPref')
      if (externalHotelId) {
        setFormData({
          bookingEngine: BEs.karismahotels,
          externalHotelId: externalHotelId,
          pms: PMSs.destiworld
        })
      }
    }

    const regexForLarkBE =
      /hotels\/([^/]+)\/(book-now-avail|booking-availability)\?.*idHotel=(\d+)/

    const match = value.match(regexForLarkBE)

    if (match) {
      setFormData({
        bookingEngine: BEs.lark,
        externalHotelId: match[3],
        bookingEngineSlug: `${match[1]}/${match[2]}`
      })
    }

    const isSynxis = value.search(/adult=\d.*arrive=.*chain=/)

    if (isSynxis === -1) {
      return
    } else {
      setFormData({
        externalHotelId: value.match(/hotel=(\d+)/)[1],
        bookingEngine: BEs.synxis,
        bookingEngineSlug: `${value.match(/\/\/(.+)\//)[1]}/?chain=${
          value.match(/chain=(\d+)/)[1]
        }`,
        bookingEngineLink: value
      })
    }
  }

  function handleHotelAddress(payload) {
    if (payload == null) {
      setFormData({ streetAddress: '', placesId: '' })
      return
    }

    const {
      hotelName,
      city,
      stateCode,
      countryCode,
      postalCode,
      streetAddress,
      placesId
    } = payload
    const fullHotelName = !hotelName.includes(city) ? `${hotelName} ${city}` : hotelName

    setFormData({
      hotelName: fullHotelName,
      city,
      stateCode,
      countryCode,
      postalCode,
      streetAddress,
      placesId
    })
  }

  const [addHotelNameManually, setAddHotelNameManually] = useState(false)

  const allStates = { ...usStates, ...canadianProvinces }

  const isStreetAddressMissing = !String(streetAddress ?? '').trim()

  return (
    <FormGroup title="AutoInvite info" done={valid}>
      <div className={styles.container}>
        <ShortLinkInput
          shortLinkError={shortLinkError}
          setShortLinkError={setShortLinkError}
          valid={shortLinkValid}
          setShortLinkValid={setShortLinkValid}
          value={urlKey}
          onChange={handleFormChange}
          chainCode={chainCode}
          setFormData={setFormData}
          isDisabled={false}
        />
        <ShortLinkHelper>
          {chainCode}/{urlKey}
        </ShortLinkHelper>
        <br />
        <br />
        <div>
          {addHotelNameManually ? (
            <>
              <TextInput
                label="Hotel name"
                value={hotelName}
                name="hotelName"
                onChange={handleFormChange}
                style={{ width: 320 }}
                valid={hotelName && !!hotelName}
                type="search"
              />
              <TextInput
                label="Street address"
                value={streetAddress}
                name="streetAddress"
                onChange={handleFormChange}
                style={{ width: 400, marginTop: 12 }}
                valid={!isStreetAddressMissing}
                type="text"
              />
              <Dropdown
                options={allStates}
                label="State"
                defaultVal="Select state"
                name="stateCode"
                value={stateCode}
                onChange={handleFormChange}
                valid={!!stateCode}
              />
            </>
          ) : (
            <label data-testid="hotelNameAndAddress">
              <span className={styles.spanLabel}>Hotel name &amp; address</span>
              <HotelAutocomplete
                onChange={handleHotelAddress}
                defaultValue={`${hotelName}, ${streetAddress}, ${city}, ${stateCode}, ${countryCode}`}
              />
            </label>
          )}
          {isStreetAddressMissing && (
            <div className={styles.validation} role="alert">
              Street number is empty
            </div>
          )}
          <br />
          <Toggle
            className={styles.toggle}
            label="Add manually"
            checked={addHotelNameManually}
            handleChange={e => setAddHotelNameManually(e.target.checked)}
          />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <ReservationHotlineInput
            valid={isPhoneValid}
            setPhoneValid={setPhoneValid}
            value={reservationHotLine}
            onChange={handleFormChange}
            setFormData={setFormData}
            isDisabled={limited}
          />
          <TASearch
            value={reviewTripAdvisorPage}
            setFormData={setFormData}
            valid={reviewTripAdvisorPage ? true : ''}
            hotelName={hotelName}
          />
        </div>
        <br />
        {parentChainCode === 'independent' ? (
          <TextInput
            label="Booking engine URL"
            style={{ width: 400 }}
            onChange={handleFormChange}
            onBlur={handleIndieBEurl}
            name="bookingEngineLink"
            value={bookingEngineLink}
          ></TextInput>
        ) : (
          <>
            {parentChainCode !== parentChains.sonesta && (
              <SearchEngines
                label="Booking engine URL"
                style={{ width: 400 }}
                value={bookingEngineLink}
                setFormData={setFormData}
                parentChainCode={parentChainCode}
                hotelName={hotelName}
                readOnly
              />
            )}
            {parentChainCode === parentChains.sonesta && (
              <TextInput
                label="Booking engine URL"
                style={{ width: 400 }}
                onChange={handleFormChange}
                onBlur={handleIndieBEurl}
                name="bookingEngineLink"
                value={bookingEngineLink}
              ></TextInput>
            )}
          </>
        )}
        <br />
        <br />
        <div style={{ display: 'flex', gap: 10 }}>
          <TextInput
            label="Hotel external ID"
            value={externalHotelId}
            onChange={handleFormChange}
            name="externalHotelId"
            valid={externalHotelId && !!externalHotelId}
          />
          <Dropdown
            options={PMSnames}
            label="PMS"
            defaultVal="Select PMS"
            name="pms"
            onChange={handleFormChange}
            value={pms}
            valid={pms && !!pms}
          />
          <TextInput
            type="date"
            label="Service start date"
            className={styles.input}
            min={moment().add(7, 'days').format('YYYY-MM-DD')}
            name="serviceStartDate"
            value={moment(serviceStartDate).format('YYYY-MM-DD')}
            onChange={e => setFormData({ serviceStartDate: e.target.value })}
          />
        </div>
        <br />

        <div style={{ display: 'flex', gap: 10 }}>
          <Dropdown
            options={getBEs(parentChainCode)}
            key={parentChainCode}
            label="Booking engine"
            name="bookingEngine"
            onChange={handleFormChange}
            defaultVal="Select booking engine"
            value={bookingEngine}
            valid={bookingEngine && !!bookingEngine}
          />
          <Dropdown
            label="Locality"
            name="cultureCode"
            defaultVal="Select locality"
            value={cultureCode}
            valid={true}
            onChange={handleFormChange}
            options={Object.keys(supportedLocales).reduce(
              (prev, key) => ({ ...prev, [key]: supportedLocales[key].englishName }),
              {}
            )}
          />
          {bookingEngineSlug && (
            <TextInput disabled value={bookingEngineSlug} label="BE slug" />
          )}
        </div>
        <br />
        <div style={{ display: 'flex', gap: 10 }}>
          <TextInput
            value={primaryWebsiteUrl}
            name="primaryWebsiteUrl"
            label="Website URL"
            onChange={handleFormChange}
            className={styles.fullWidthInput}
          />
        </div>
      </div>
    </FormGroup>
  )
}

export default AutoInviteInfo
